// Per-row NSB selection store. Keyed by sbKey (`${request.id}_${lineItemId}`), it records each row's
// explicit override per column — a smart-baseline flavor id (e.g. 'F3') or 'manual'. Rows that follow
// the event-level default have no entry. This is the single source the save path reads to persist
// `line_items.nsb_row_config`, and what hydration repopulates on load so a row's override survives a
// reload instead of being re-seeded back to the event default (the clobber bug).
//
// It lives NEXT TO the renderers but imports none of them: the NP/BP renderers own their own applied-
// state maps (appliedSmartBaselineKeys, baselineUserFlavorBySbKey, baselineManualKeys) which cannot be
// imported across files without an import cycle — so, mirroring nsbEventReset, each renderer REGISTERS
// a hydrator here that repopulates its own maps. This module is therefore safe to import from the save
// path (EventsServices) and the provider (hydration) without dragging in the renderer graph.
import { ID, NsbRowConfig } from 'types';
import DatadogService from '@services/DatadogService';
import { registerNsbEventResetter } from './nsbEventReset';

export type NsbRowColumn = 'baseline' | 'negotiation';

const store = new Map<string, NsbRowConfig>();

// Subscribers (e.g. the match-stats spend line) re-read after any change. Needed because a manual
// override is written here from a renderer effect that runs AFTER consumers have rendered for that
// edit — without a notify, the spend stat would read the store a beat too early and never re-run.
const subscribers = new Set<() => void>();

// Coalesced into a microtask. NsbMatchStats subscribes and walks every row's spend on each notify, so a
// bulk write path — upload, add-lot, or an event-method reseed across 1000 rows — would otherwise be
// N notifies x O(n) work. Any burst within a tick becomes one subscriber round.
let notifyScheduled = false;
const notify = (): void => {
  if (notifyScheduled) return;
  notifyScheduled = true;
  queueMicrotask(() => {
    notifyScheduled = false;
    subscribers.forEach(fn => fn());
  });
};
export const subscribeNsbRowConfig = (fn: () => void): (() => void) => {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
};

export const getNsbRowConfig = (sbKey: string): NsbRowConfig | undefined => store.get(sbKey);

// Rows whose source has been WRITTEN since the last hydration, with the lot each belongs to. The save
// path reads this instead of walking every lot x line item: a save that changed only a lot name would
// otherwise rebuild and compare a source object for every row in the event, since the seeder records an
// auto source for each row it seeds. Marked inside the writers below rather than at their call sites —
// a caller that forgot would silently fail to persist that row's source.
const dirtyByKey = new Map<string, ID>();

// A row with no lot cannot be attached to a save payload, but dropping it here would silently fail to
// persist its source — the very thing the required argument exists to prevent. Record it and report it;
// the save path skips entries with no lot, so the loss is visible rather than invisible.
const markDirty = (sbKey: string, lotId: ID): void => {
  if (lotId === undefined || lotId === null || lotId === '') {
    DatadogService.captureErrorMessage(`nsbRowConfigStore: row ${sbKey} written with no lot id; its source cannot be saved`);
    return;
  }
  dirtyByKey.set(sbKey, lotId);
};

export interface DirtyNsbRow {
  lineItemId: string;
  lotId: ID;
  sbKey: string;
}

// Line item ids come from the sbKey's `${eventId}_${lineItemId}` form, same as getBuyerPickedLineItemIds.
export const getDirtyNsbRows = (): DirtyNsbRow[] => (
  Array.from(dirtyByKey.entries()).map(([sbKey, lotId]) => ({
    lineItemId: sbKey.slice(sbKey.indexOf('_') + 1),
    lotId,
    sbKey,
  })).filter(({ lineItemId }) => Boolean(lineItemId))
);

// What the SEEDER applied to a row following the event-level default. Separate from the explicit
// overrides above, because an override survives an event-level method change and an auto source does not.
const autoStore = new Map<string, NsbRowConfig>();

export const setNsbRowAutoSource = (sbKey: string | null, column: NsbRowColumn, value: string, lotId: ID): void => {
  if (!sbKey) return;
  const current = autoStore.get(sbKey);
  if (current?.[column] === value) return; // already recorded — keep this idempotent for redraws
  autoStore.set(sbKey, { ...current, [column]: value });
  markDirty(sbKey, lotId);
};

// Explicit override where there is one, otherwise whatever the seeder applied — per column.
export const getNsbRowSourcesForSave = (sbKey: string): NsbRowConfig | undefined => {
  const explicit = store.get(sbKey);
  const auto = autoStore.get(sbKey);
  if (!explicit && !auto) return undefined;

  return { ...auto, ...explicit };
};

// What each row's sources were at hydration, so the save path can send only rows that actually changed.
// Without this, a 1000-row event carries ~1000 line-item entries on every save — including a save that
// only renamed a lot — and the backend re-touches each one.
const hydratedSources = new Map<string, NsbRowConfig>();

// True when a row's sources are identical to what hydration restored, i.e. there is nothing to persist.
// A row with NO snapshot is deliberately NOT "unchanged": a new item, or any row an event hydrated
// nothing for, must still be sent or it would never persist a source at all.
// Pass `sources` when the caller already has them: the save path walks every row, so recomputing here
// would allocate a second throwaway object per row.
export const isNsbRowSourceUnchanged = (sbKey: string, sources?: NsbRowConfig): boolean => {
  const snapshot = hydratedSources.get(sbKey);
  if (!snapshot) return false;
  const current = sources ?? getNsbRowSourcesForSave(sbKey);
  if (!current) return false;

  return (current.baseline ?? null) === (snapshot.baseline ?? null)
    && (current.negotiation ?? null) === (snapshot.negotiation ?? null);
};

// Record a row's override for one column (the other column's value is preserved). Called from the
// quick picker, the intel modal, and the renderers' manual-entry detectors.
export const setNsbRowConfigColumn = (sbKey: string | null, column: NsbRowColumn, value: string, lotId: ID): void => {
  if (!sbKey) return;
  store.set(sbKey, { ...store.get(sbKey), [column]: value });
  markDirty(sbKey, lotId);
  notify();
};

// Drop a row's override for one column (e.g. a manual baseline reverting to the flavor price). Removes
// the whole entry once both columns are clear.
export const clearNsbRowConfigColumn = (sbKey: string | null, column: NsbRowColumn, lotId: ID): void => {
  if (!sbKey) return;
  const current = store.get(sbKey);
  if (!current || current[column] === undefined) return;
  const next = { ...current };
  delete next[column];
  if (Object.keys(next).length === 0) store.delete(sbKey);
  else store.set(sbKey, next);
  markDirty(sbKey, lotId);
  notify();
};

// What a renderer's hydrator receives per row on load: the persisted config plus the saved prices, so
// it can rebuild its applied-state label (and the price it points at) without re-deriving from search.
export interface NsbRowHydrationEntry {
  baselinePrice?: number | string | null;
  // True when the buyer resolved this row by hand at the last save. Every row now carries a saved label,
  // so the label alone cannot tell a buyer's choice from the app's; this is what does.
  buyerPicked?: boolean;
  config: NsbRowConfig;
  // True when the negotiation source is the backend's ICO fallback on a still-unpriced row of a draft
  // event (see nsbIcoFallbackStore). Such a row is left unsettled so a smart baseline match can replace it.
  icoFallback?: boolean;
  negotiationPrice?: number | string | null;
  sbKey: string;
}

const hydrators = new Set<(entries: NsbRowHydrationEntry[]) => void>();

// A renderer registers a callback (at module load) that repopulates ITS OWN applied-state maps from the
// hydration entries — see registerNsbEventResetter for the same indirection and why it's needed.
export const registerNsbRowHydrator = (fn: (entries: NsbRowHydrationEntry[]) => void): void => {
  hydrators.add(fn);
};

// Restore the per-row store + every renderer's applied-state on event load, BEFORE rows render, so the
// auto-seed guards see the overrides and skip them (no clobber) and the cells show the right labels.
export const hydrateNsbRowConfigs = (entries: NsbRowHydrationEntry[]): void => {
  entries.forEach(({ buyerPicked, config, sbKey }) => {
    if (!sbKey || !config) return;
    // Back into the store it came from, or the next save re-records every row as the buyer's.
    if (buyerPicked) store.set(sbKey, config);
    else autoStore.set(sbKey, config);
    hydratedSources.set(sbKey, config);
  });
  // Forget only the rows hydration actually reconciled. This runs on the refetch after every save and
  // carries only rows that came back with a source, so clearing wholesale would drop the mark for a row
  // the buyer edited while that refetch was in flight — and its source would never be saved.
  Array.from(dirtyByKey.keys()).forEach(sbKey => {
    if (isNsbRowSourceUnchanged(sbKey)) dirtyByKey.delete(sbKey);
  });
  hydrators.forEach(fn => fn(entries));
  notify();
};

// Line item ids, taken from the sbKey's `${eventId}_${lineItemId}` form.
export const getBuyerPickedLineItemIds = (): string[] => (
  Array.from(store.keys()).map(sbKey => sbKey.slice(sbKey.indexOf('_') + 1)).filter(Boolean)
);

// Cleared on every event change (see nsbEventReset) so a new event starts with no per-row overrides.
registerNsbEventResetter(() => {
  store.clear();
  autoStore.clear();
  hydratedSources.clear();
  dirtyByKey.clear();
  notify();
});
